import React from 'react'
import { graphql } from 'gatsby'
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews'

import { Layout } from '../components/Layout'
import { components } from '../slices'
import SliceZoneComponent from '../components/SliceZone'
import { getDefaultPageData } from '../utils/getDefaultPageData'
import { SEO } from '../components/Head'

const PageTemplate = ({ data }) => {
  if (!data) return null

  const filtersData = data.prismicFilters.data
  const stories = data.allPrismicStoryPage.nodes
  const headerArticlesData = data.headerArticles.nodes

  const { topLine, footer, header, page, activeDoc, topMenu } =
    getDefaultPageData(data, 'prismicPage')

  return (
    <Layout
      topMenu={topMenu.data}
      footer={footer.data}
      topLine={topLine.data}
      header={{ nav: header.data.nav, articles: header.data.articles }}
      activeDocMeta={activeDoc}
      headerArticlesData={headerArticlesData}
    >
      <SliceZoneComponent
        slices={page.body}
        components={components}
        context={{ filtersData, stories }}
      />
    </Layout>
  )
}

export const Head = ({ data }) => {
  const { page, pageContent } = getDefaultPageData(data, 'prismicPage')
  return (
    <SEO
      meta={{
        title: page?.meta_title,
        description: page?.meta_description,
        image: page?.meta_image,
        type: page?.type,
        pageContent,
      }}
    />
  )
}

export const query = graphql`
  query pageQuery($id: String, $lang: String) {
    prismicPage(id: { eq: $id }, lang: { eq: $lang }) {
      _previewable
      url
      uid
      type
      id
      lang
      alternate_languages {
        id
        type
        lang
        uid
      }
      data {
        meta_title {
          text
        }
        meta_image {
          url
          alt
        }
        meta_description {
          text
        }
        body {
          ... on PrismicSlice {
            id
            slice_label
            slice_type
          }
          ...PageDataBodyMainWithSlider
          ...PageDataBodyResults
          ...PageDataBodyBusinessesAction
          ...PageDataBodyStoriesSlider
          ...PageDataBodyRecentSuccessStories
          ...PageDataBodyCallToAction
        }
      }
    }
    prismicTopLine(lang: { eq: $lang }) {
      ...TopLineFragment
    }
    prismicHeader(lang: { eq: $lang }) {
      data {
        articles {
          article_link {
            document {
              ... on PrismicArticlePage {
                ...ArticlePage
              }
            }
          }
        }
        nav {
          ... on PrismicSlice {
            id
            slice_type
            slice_label
          }
          ...HeaderDataNavFooter
          ...HeaderDataNavButton
          ...HeaderDataNavNavItem
        }
      }
    }
    prismicFooter(lang: { eq: $lang }) {
      data {
        body {
          ... on PrismicSlice {
            id
            slice_type
            slice_label
          }
          ...FooterDataBodyCopyright
          ...FooterDataBodyFooter
          ...FooterDataBodySocials
          ...FooterDataBodyAwards
          ...FooterDataBodyForm
        }
        body1 {
          ... on PrismicSlice {
            id
            slice_type
            slice_label
          }
          ...FooterDataBody1NavItem
        }
      }
    }
    prismicFilters(lang: { eq: $lang }, uid: { eq: "story-filters" }) {
      data {
        first_filter {
          richText
        }
        first_filter_default_value {
          text
        }
        second_filter {
          richText
        }
        second_filter_default_value {
          text
        }
        title {
          text
        }
      }
    }
    allPrismicStoryPage(filter: { lang: { eq: $lang } }) {
      nodes {
        data {
          title {
            text
          }
          label {
            text
          }
          person {
            document {
              ... on PrismicPerson {
                id
                data {
                  name
                  photo {
                    gatsbyImageData
                    alt
                  }
                  quote {
                    text
                  }
                  position
                }
              }
            }
          }
          brand {
            document {
              ... on PrismicBrand {
                id
                data {
                  image {
                    gatsbyImageData
                    alt
                  }
                }
              }
            }
          }
          video_preview {
            alt
            gatsbyImageData
          }
          results {
            big_label {
              text
            }
            text {
              text
            }
          }
          solution
          industry
        }
        uid
        url
      }
    }
    headerArticles: allPrismicArticlePage(
      filter: {
        lang: { eq: $lang }
        data: { invisible: { eq: "no" }, type: { eq: "blog" } }
      }
      limit: 2
      sort: { first_publication_date: DESC }
    ) {
      nodes {
        ...ArticlePage
      }
    }
  }
`

export default withPrismicPreview(PageTemplate)
